import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  SashiganeGenerator, SashiganePuzzle, GridClue, ArrowDir,
  validateUserSolution, solutionBorders, computeRegionMap, checkLShape, arrowTowardElbow,
} from '../../utils/sashiganeEngine';
import VictoryModal from '../../components/game/VictoryModal';
import HowToPlayModal from '../../components/game/HowToPlayModal';
import { useGameProgress } from '../../hooks/useGameProgress';
import { useSessionContext } from '../../hooks/useSessionContext';
import SessionBanner from '../../components/game/SessionBanner';

const generator = new SashiganeGenerator();

type BorderState = { h: boolean[][]; v: boolean[][] };
type HintEdge = { axis: 'h' | 'v'; r: number; c: number } | null;

function emptyBorders(n: number): BorderState {
  return {
    h: Array.from({ length: n - 1 }, () => Array(n).fill(false)),
    v: Array.from({ length: n }, () => Array(n - 1).fill(false)),
  };
}

function cloneBorders(b: BorderState): BorderState {
  return { h: b.h.map(row => [...row]), v: b.v.map(row => [...row]) };
}

// ─── Rule-based error detector (no solution comparison) ───────────────────────

function calcularErrores(borders: BorderState, puzzle: SashiganePuzzle): Set<string> {
  const { n, clues } = puzzle;
  const errores = new Set<string>();

  const userMap = computeRegionMap(n, borders.h, borders.v);

  const groups = new Map<number, { r: number; c: number }[]>();
  for (let r = 0; r < n; r++)
    for (let c = 0; c < n; c++) {
      const id = userMap[r][c];
      if (!groups.has(id)) groups.set(id, []);
      groups.get(id)!.push({ r, c });
    }

  const clueMap = new Map<string, GridClue>();
  clues.forEach(cl => clueMap.set(`${cl.r},${cl.c}`, cl));

  for (const [, cells] of groups) {
    const markAll = () => cells.forEach(({ r, c }) => errores.add(`${r},${c}`));

    if (cells.length >= 3) {
      const { valid, elbow } = checkLShape(cells);

      if (!valid) {
        markAll();
        continue;
      }

      // Valid L-shape — check clue consistency
      if (elbow) {
        for (const { r, c } of cells) {
          const cl = clueMap.get(`${r},${c}`);
          if (!cl) continue;

          if (cl.kind === 'elbow') {
            // Elbow clue must sit at the detected elbow
            if (r !== elbow.r || c !== elbow.c) {
              errores.add(`${r},${c}`);
            }
            // Region already bigger than the stated size
            if (cl.num !== undefined && cells.length > cl.num) {
              markAll();
            }
          } else {
            // Arrow: must be at an endpoint (exactly 1 region-neighbor)
            const nbCount = cells.filter(o =>
              Math.abs(o.r - r) + Math.abs(o.c - c) === 1
            ).length;
            if (nbCount !== 1) {
              errores.add(`${r},${c}`);
            } else if (arrowTowardElbow({ r, c }, elbow) !== cl.dir) {
              // Endpoint exists but arrow direction is wrong
              errores.add(`${r},${c}`);
            }
          }
        }
      }
    } else {
      // Regions with 1–2 cells: only flag obvious clue violations
      for (const { r, c } of cells) {
        const cl = clueMap.get(`${r},${c}`);
        if (!cl) continue;
        // Numbered elbow already exceeded (can't happen for size<3 since min L=3, but guard anyway)
        if (cl.kind === 'elbow' && cl.num !== undefined && cells.length > cl.num) {
          markAll();
        }
        // Arrow already has 2+ region-neighbors → not an endpoint
        if (cl.kind === 'arrow') {
          const nbCount = cells.filter(o =>
            Math.abs(o.r - r) + Math.abs(o.c - c) === 1
          ).length;
          if (nbCount >= 2) errores.add(`${r},${c}`);
        }
      }
    }
  }

  return errores;
}

// ─── Arrow SVG ────────────────────────────────────────────────────────────────

function ArrowSymbol({ dir, cx, cy, size }: { dir: ArrowDir; cx: number; cy: number; size: number }) {
  const h = size * 0.4;
  const w = size * 0.35;
  let points = '';
  switch (dir) {
    case 'U': points = `${cx},${cy - h} ${cx - w},${cy + h * 0.5} ${cx + w},${cy + h * 0.5}`; break;
    case 'D': points = `${cx},${cy + h} ${cx - w},${cy - h * 0.5} ${cx + w},${cy - h * 0.5}`; break;
    case 'L': points = `${cx - h},${cy} ${cx + h * 0.5},${cy - w} ${cx + h * 0.5},${cy + w}`; break;
    case 'R': points = `${cx + h},${cy} ${cx - h * 0.5},${cy - w} ${cx - h * 0.5},${cy + w}`; break;
  }
  return <polygon points={points} fill="#374151" />;
}

// ─── Board SVG ────────────────────────────────────────────────────────────────

interface BoardProps {
  puzzle: SashiganePuzzle;
  borders: BorderState;
  errorCells: Set<string>;   // cells in rule-violating regions → red background
  hintEdge: HintEdge;        // recently hinted edge → yellow flash
  isCompleted: boolean;
  onToggleH: (r: number, c: number) => void;
  onToggleV: (r: number, c: number) => void;
  width: number;
  height: number;
}

function Board({ puzzle, borders, errorCells, hintEdge, isCompleted, onToggleH, onToggleV, width, height }: BoardProps) {
  const { n, clues } = puzzle;
  const CS = Math.min(width / n, height / n);
  const W = CS * n;
  const H = CS * n;
  const EDGE_HIT = Math.max(8, CS * 0.15);
  const DRAWN = 3;
  const FAINT = 0.5;

  const clueMap = new Map<string, GridClue>();
  clues.forEach(cl => clueMap.set(`${cl.r},${cl.c}`, cl));

  const cellFill = (r: number, c: number): string => {
    if (isCompleted) return '#dcfce7';
    if (errorCells.has(`${r},${c}`)) return '#fecaca';
    return '#ffffff';
  };

  const hEdgeColor = (r: number, c: number): string => {
    if (hintEdge?.axis === 'h' && hintEdge.r === r && hintEdge.c === c) return '#f59e0b';
    return '#1e293b';
  };
  const vEdgeColor = (r: number, c: number): string => {
    if (hintEdge?.axis === 'v' && hintEdge.r === r && hintEdge.c === c) return '#f59e0b';
    return '#1e293b';
  };

  return (
    <svg width={W} height={H} style={{ display: 'block', margin: '0 auto', userSelect: 'none' }}>
      {/* Cell backgrounds */}
      {Array.from({ length: n }, (_, r) =>
        Array.from({ length: n }, (_, c) => (
          <rect key={`bg-${r}-${c}`} x={c * CS} y={r * CS} width={CS} height={CS}
            fill={cellFill(r, c)} />
        ))
      )}

      {/* Faint grid lines */}
      {Array.from({ length: n - 1 }, (_, r) =>
        Array.from({ length: n }, (_, c) => (
          <line key={`fh-${r}-${c}`}
            x1={c * CS} y1={(r + 1) * CS} x2={(c + 1) * CS} y2={(r + 1) * CS}
            stroke="#d1d5db" strokeWidth={FAINT} />
        ))
      )}
      {Array.from({ length: n }, (_, r) =>
        Array.from({ length: n - 1 }, (_, c) => (
          <line key={`fv-${r}-${c}`}
            x1={(c + 1) * CS} y1={r * CS} x2={(c + 1) * CS} y2={(r + 1) * CS}
            stroke="#d1d5db" strokeWidth={FAINT} />
        ))
      )}

      {/* User-drawn horizontal borders */}
      {Array.from({ length: n - 1 }, (_, r) =>
        Array.from({ length: n }, (_, c) =>
          borders.h[r][c] ? (
            <line key={`h-${r}-${c}`}
              x1={c * CS} y1={(r + 1) * CS} x2={(c + 1) * CS} y2={(r + 1) * CS}
              stroke={hEdgeColor(r, c)} strokeWidth={DRAWN} strokeLinecap="square" />
          ) : null
        )
      )}

      {/* User-drawn vertical borders */}
      {Array.from({ length: n }, (_, r) =>
        Array.from({ length: n - 1 }, (_, c) =>
          borders.v[r][c] ? (
            <line key={`v-${r}-${c}`}
              x1={(c + 1) * CS} y1={r * CS} x2={(c + 1) * CS} y2={(r + 1) * CS}
              stroke={vEdgeColor(r, c)} strokeWidth={DRAWN} strokeLinecap="square" />
          ) : null
        )
      )}

      {/* Outer border */}
      <rect x={0} y={0} width={W} height={H}
        fill="none" stroke="#1e293b" strokeWidth={5} />

      {/* Clue overlays */}
      {Array.from({ length: n }, (_, r) =>
        Array.from({ length: n }, (_, c) => {
          const cl = clueMap.get(`${r},${c}`);
          if (!cl) return null;
          const cx = c * CS + CS / 2;
          const cy = r * CS + CS / 2;
          const R = CS * 0.32;
          if (cl.kind === 'elbow') {
            return (
              <g key={`el-${r}-${c}`}>
                <circle cx={cx} cy={cy} r={R}
                  fill={isCompleted ? '#22c55e' : '#667eea'} stroke="white" strokeWidth={1.5} />
                {cl.num !== undefined && (
                  <text x={cx} y={cy + 1} textAnchor="middle" dominantBaseline="middle"
                    fill="white" fontSize={Math.max(10, R * 1.1)} fontWeight="bold"
                    style={{ pointerEvents: 'none' }}>
                    {cl.num}
                  </text>
                )}
              </g>
            );
          }
          return (
            <g key={`ar-${r}-${c}`}>
              <ArrowSymbol dir={cl.dir!} cx={cx} cy={cy} size={CS} />
            </g>
          );
        })
      )}

      {/* Clickable horizontal edges */}
      {!isCompleted && Array.from({ length: n - 1 }, (_, r) =>
        Array.from({ length: n }, (_, c) => (
          <rect key={`ch-${r}-${c}`}
            x={c * CS} y={(r + 1) * CS - EDGE_HIT / 2} width={CS} height={EDGE_HIT}
            fill="transparent" style={{ cursor: 'pointer' }}
            onClick={() => onToggleH(r, c)} />
        ))
      )}

      {/* Clickable vertical edges */}
      {!isCompleted && Array.from({ length: n }, (_, r) =>
        Array.from({ length: n - 1 }, (_, c) => (
          <rect key={`cv-${r}-${c}`}
            x={(c + 1) * CS - EDGE_HIT / 2} y={r * CS} width={EDGE_HIT} height={CS}
            fill="transparent" style={{ cursor: 'pointer' }}
            onClick={() => onToggleV(r, c)} />
        ))
      )}
    </svg>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const difficulties = [
  { orden: 1, nombre: 'Neurona',   icono: '🧠' },
  { orden: 2, nombre: 'Sinapsis',  icono: '⚡' },
  { orden: 3, nombre: 'Corteza',   icono: '🧩' },
  { orden: 4, nombre: 'Genio',     icono: '💡' },
  { orden: 5, nombre: 'Einstein',  icono: '🎓' },
];

export default function SashiganeGame() {
  const { dificultad } = useParams<{ dificultad: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const dif = parseInt(dificultad || '1');
  const { saveProgress: saveGameProgress } = useGameProgress('sashigane', dif);
  const { isInSession, sessionType, saveSessionResult } = useSessionContext();

  const [puzzle, setPuzzle] = useState<SashiganePuzzle | null>(null);
  const [borders, setBorders] = useState<BorderState>({ h: [], v: [] });
  const [history, setHistory] = useState<BorderState[]>([]);
  const [histIdx, setHistIdx] = useState(-1);
  const [isCompleted, setIsCompleted] = useState(false);
  const [showVictory, setShowVictory] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modoAsistido, setModoAsistido] = useState(false);
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [moveCount, setMoveCount] = useState(0);
  const [forceNew, setForceNew] = useState(0);
  const [hintEdge, setHintEdge] = useState<HintEdge>(null);

  const boardAreaRef = useRef<HTMLDivElement>(null);
  const [boardW, setBoardW] = useState(0);
  const [boardH, setBoardH] = useState(0);

  // Timer
  useEffect(() => {
    if (isCompleted) return;
    const interval = setInterval(() => setElapsedTime(t => t + 1), 1000);
    return () => clearInterval(interval);
  }, [isCompleted]);

  const formatTime = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const measureBoard = useCallback(() => {
    if (boardAreaRef.current) {
      const { clientWidth, clientHeight } = boardAreaRef.current;
      setBoardW(clientWidth - 16);
      setBoardH(clientHeight - 16);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('resize', measureBoard);
    return () => window.removeEventListener('resize', measureBoard);
  }, [measureBoard]);

  // Generate puzzle
  useEffect(() => {
    setLoading(true);
    setError('');
    try {
      const seedParam = searchParams.get('seed') ? parseInt(searchParams.get('seed')!) : undefined;
      const isNew = forceNew > 0;
      const p = generator.generate(dif, isNew ? undefined : seedParam);
      setPuzzle(p);
      setSearchParams({ seed: p.seed.toString() }, { replace: true });
      const empty = emptyBorders(p.n);
      setBorders(empty);
      setHistory([cloneBorders(empty)]);
      setHistIdx(0);
      setMoveCount(0);
      setElapsedTime(0);
      setIsCompleted(false);
      setShowVictory(false);
      setModoAsistido(false);
      setHintEdge(null);
      requestAnimationFrame(measureBoard);
    } catch (e: any) {
      setError(e.message || 'Error al generar el puzzle');
    } finally {
      setLoading(false);
    }
  }, [dif, forceNew]);

  useEffect(() => { if (!loading) requestAnimationFrame(measureBoard); }, [loading, measureBoard]);

  const saveHistoryState = (newBorders: BorderState) => {
    const snap = cloneBorders(newBorders);
    const newHist = history.slice(0, histIdx + 1);
    newHist.push(snap);
    if (newHist.length > 80) newHist.shift();
    setHistory(newHist);
    setHistIdx(newHist.length - 1);
  };

  const applyBorders = (newBorders: BorderState) => {
    if (!puzzle) return;
    setBorders(newBorders);
    setMoveCount(m => m + 1);
    saveHistoryState(newBorders);
    if (validateUserSolution(puzzle.n, newBorders.h, newBorders.v, puzzle)) {
      setIsCompleted(true);
      setShowVictory(true);
      saveGameProgress({ tiempo: elapsedTime, movimientos: moveCount + 1 });
      if (isInSession) saveSessionResult({ tiempo: elapsedTime, movimientos: moveCount + 1, completado: true });
    }
  };

  const onToggleH = (r: number, c: number) => {
    if (isCompleted || !puzzle) return;
    const nb = cloneBorders(borders);
    nb.h[r][c] = !nb.h[r][c];
    applyBorders(nb);
  };

  const onToggleV = (r: number, c: number) => {
    if (isCompleted || !puzzle) return;
    const nb = cloneBorders(borders);
    nb.v[r][c] = !nb.v[r][c];
    applyBorders(nb);
  };

  const flash = (edge: HintEdge) => {
    setHintEdge(edge);
    setTimeout(() => setHintEdge(null), 1200);
  };

  const handleHint = () => {
    if (!puzzle || isCompleted) return;
    const sol = solutionBorders(puzzle);
    const nb = cloneBorders(borders);

    // Priority 1: remove a wrong border (drawn but not in solution)
    for (let r = 0; r < puzzle.n - 1; r++) {
      for (let c = 0; c < puzzle.n; c++) {
        if (nb.h[r][c] && !sol.h[r][c]) {
          nb.h[r][c] = false;
          flash({ axis: 'h', r, c });
          applyBorders(nb);
          return;
        }
      }
    }
    for (let r = 0; r < puzzle.n; r++) {
      for (let c = 0; c < puzzle.n - 1; c++) {
        if (nb.v[r][c] && !sol.v[r][c]) {
          nb.v[r][c] = false;
          flash({ axis: 'v', r, c });
          applyBorders(nb);
          return;
        }
      }
    }

    // Priority 2: reveal a random missing correct border
    const missing: HintEdge[] = [];
    for (let r = 0; r < puzzle.n - 1; r++)
      for (let c = 0; c < puzzle.n; c++)
        if (!nb.h[r][c] && sol.h[r][c]) missing.push({ axis: 'h', r, c });
    for (let r = 0; r < puzzle.n; r++)
      for (let c = 0; c < puzzle.n - 1; c++)
        if (!nb.v[r][c] && sol.v[r][c]) missing.push({ axis: 'v', r, c });

    if (missing.length === 0) {
      setError('¡Ya está todo correcto!');
      setTimeout(() => setError(''), 2000);
      return;
    }

    const pick = missing[Math.floor(Math.random() * missing.length)]!;
    if (pick.axis === 'h') nb.h[pick.r][pick.c] = true;
    else nb.v[pick.r][pick.c] = true;
    flash(pick);
    applyBorders(nb);
  };

  const handleShowSolution = () => {
    if (!puzzle || !window.confirm('¿Estás seguro? Esto mostrará la solución completa.')) return;
    const sol = solutionBorders(puzzle);
    applyBorders(sol);
  };

  const onUndo = () => {
    if (histIdx <= 0) return;
    setBorders(cloneBorders(history[histIdx - 1]));
    setHistIdx(histIdx - 1);
  };

  const onRedo = () => {
    if (histIdx >= history.length - 1) return;
    setBorders(cloneBorders(history[histIdx + 1]));
    setHistIdx(histIdx + 1);
  };

  const onReset = () => {
    if (!puzzle || !window.confirm('¿Reiniciar el tablero?')) return;
    const empty = emptyBorders(puzzle.n);
    setBorders(empty);
    saveHistoryState(empty);
    setIsCompleted(false);
    setShowVictory(false);
    setHintEdge(null);
  };

  const onNewBoard = () => {
    setSearchParams({});
    setForceNew(f => f + 1);
  };

  // Modo asistido: rule-based errors (no solution comparison)
  const errorCells = (modoAsistido && puzzle)
    ? calcularErrores(borders, puzzle)
    : new Set<string>();

  if (loading) {
    return (
      <div className="h-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
        <div className="text-white text-2xl">🔄 Generando puzzle…</div>
      </div>
    );
  }

  if (!puzzle) {
    return (
      <div className="h-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="text-4xl mb-4">⚠️</div>
          <p>{error || 'Error desconocido'}</p>
          <button onClick={onNewBoard} className="mt-4 px-6 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg">
            Intentar de nuevo
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-gradient-to-br from-purple-600 to-blue-600 flex flex-col">
      {isInSession && sessionType && <SessionBanner sessionType={sessionType as 'presencial' | 'remota'} />}

      {/* Compact top bar */}
      <div className="bg-black/20 backdrop-blur-sm px-3 py-2 flex items-center gap-2 flex-wrap">
        <span className="text-white font-bold text-base whitespace-nowrap">📐 Sashigane</span>
        <div className="w-px h-5 bg-white/30 hidden sm:block" />
        <div className="flex gap-1 flex-wrap">
          {difficulties.map(d => (
            <button
              key={d.orden}
              onClick={() => {
                if (dif === d.orden) onNewBoard();
                else navigate(`/juegos/sashigane/${d.orden}`);
              }}
              className={`px-2 py-1 rounded-md text-xs font-semibold transition-all ${
                dif === d.orden ? 'bg-white text-purple-700' : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              {d.icono} <span className="hidden sm:inline">{d.nombre}</span>
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <span className="text-white/70 text-xs hidden sm:inline">⏱ {formatTime(elapsedTime)}</span>
        <div className="flex gap-1 items-center">
          {/* Deshacer */}
          <button onClick={onUndo} disabled={histIdx <= 0} title="Deshacer"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 7v6h6"/><path d="M21 17a9 9 0 00-9-9 9 9 0 00-6 2.3L3 13"/></svg>
          </button>
          {/* Rehacer */}
          <button onClick={onRedo} disabled={histIdx >= history.length - 1} title="Rehacer"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 7v6h-6"/><path d="M3 17a9 9 0 019-9 9 9 0 016 2.3L21 13"/></svg>
          </button>
          {/* Modo asistido */}
          <button onClick={() => setModoAsistido(v => !v)}
            title={modoAsistido ? 'Desactivar modo asistido' : 'Activar modo asistido'}
            className={`p-1.5 rounded-md transition-all text-white ${modoAsistido ? 'bg-blue-500 ring-2 ring-blue-300' : 'bg-white/20 hover:bg-white/30'}`}>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          </button>
          {/* Pista */}
          <button onClick={handleHint} disabled={isCompleted} title="Pista"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18h6M10 22h4M12 2a7 7 0 017 7c0 2.38-1.19 4.47-3 5.74V17H8v-2.26C6.19 13.47 5 11.38 5 9a7 7 0 017-7z"/></svg>
          </button>
          {/* Ver solución */}
          <button onClick={handleShowSolution} disabled={isCompleted} title="Ver solución"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
          </button>
          {/* Reiniciar */}
          <button onClick={onReset} title="Reiniciar"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12a9 9 0 109-9M3 3v6h6"/></svg>
          </button>
          {/* Nuevo tablero */}
          <button onClick={onNewBoard} title="Nuevo tablero"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M12 8v8M8 12h8"/></svg>
          </button>
          {/* Cómo se juega */}
          <button onClick={() => setShowHowToPlay(true)} title="Cómo se juega"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
          </button>
        </div>
      </div>

      {/* Error / info message */}
      {error && (
        <div className="mx-3 mt-2 p-2 bg-blue-100 border border-blue-300 text-blue-800 rounded-lg text-sm text-center">
          {error}
        </div>
      )}

      {/* Board area */}
      <div ref={boardAreaRef} className="flex-1 min-h-0 flex items-center justify-center p-2">
        {boardW > 0 && boardH > 0 && (
          <Board
            puzzle={puzzle}
            borders={borders}
            errorCells={errorCells}
            hintEdge={hintEdge}
            isCompleted={isCompleted}
            onToggleH={onToggleH}
            onToggleV={onToggleV}
            width={boardW}
            height={boardH}
          />
        )}
      </div>

      <VictoryModal
        isOpen={showVictory}
        onClose={() => setShowVictory(false)}
        stats={{ tiempo: elapsedTime, movimientos: moveCount }}
      />

      <HowToPlayModal
        isOpen={showHowToPlay}
        onClose={() => setShowHowToPlay(false)}
        gameTitle="Sashigane"
        gameIcon="📐"
        objective="Divide el tablero en regiones de forma «L» de una casilla de ancho. Cada L tiene un punto de inflexión (círculo) y sus extremos pueden llevar flechas."
        rules={[
          'Cada región debe tener forma de «L» (dos brazos rectos perpendiculares que se unen en el codo).',
          'El codo de cada «L» está marcado con un círculo. Si tiene número, indica el total de casillas de esa región.',
          'Las flechas en los extremos de los brazos apuntan hacia el codo de su región.',
          'Los círculos sin número no imponen restricción al tamaño de la región.',
          'Todo el tablero debe quedar cubierto exactamente por las regiones «L».',
        ]}
        howToPlay='Haz clic en el borde entre dos casillas para dibujar o borrar una línea. El escudo activa el modo asistido: resalta en rojo las celdas que violan alguna regla (región no L, número excedido, flecha mal colocada). La bombilla corrige un error o revela un borde correcto.'
        author="Nikoli (Japón)"
      />
    </div>
  );
}
